import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { toast } from 'sonner';
import { useActiveContext } from './useActiveContext';
import { config } from '../config';
import { User } from './useUser';

interface CreateOrgResponse {
  id: string;
  name: string;
  slug: string;
}

interface ApiErrorResponse {
  error: string;
  description?: string;
}

/**
 * Creates a new organization the current user will own (capped at one owned org
 * per user; the backend rejects a second with 400).
 *
 * Provisioning makes the new org's "Org Admin" context the user's active
 * context server-side, so on success we pull the refreshed user and sync the
 * local active-context state (axios header + sessionStorage) — landing the app
 * directly in the new org — then invalidate every context-scoped query.
 */
export function useCreateOrganization() {
  const queryClient = useQueryClient();
  const { setActiveContextId } = useActiveContext();

  return useMutation<CreateOrgResponse, AxiosError<ApiErrorResponse>, string>({
    mutationFn: async (name: string) => {
      const response = await axios.post<CreateOrgResponse>(
        `${config.backendApi}/api/organizations/`,
        { name },
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: async () => {
      try {
        // Without this tab's context: `current` answers for the context a request names,
        // which is still the one the tab was in, and the server's choice is the remembered one.
        const { data: current } = await axios.get<User>(
          `${config.backendApi}/api/auth/current`,
          { withCredentials: true, headers: { 'X-Active-Context-Id': false } },
        );
        const newContextId = current.activeContext?.id ?? current.lastActiveContextId ?? null;
        if (newContextId) {
          setActiveContextId(newContextId);
        }
      } catch {
        // Non-fatal: query invalidation below still refreshes the switcher to
        // reflect the new org even if we couldn't pre-set the active context.
      }
      void queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      void queryClient.invalidateQueries();
      toast.success('Organization created');
    },
    onError: (err) => {
      toast.error(
        err.response?.data.description ??
          err.response?.data.error ??
          'Failed to create organization',
      );
    },
  });
}
